import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setModalId,
  usePublicState,
} from '../../../../store/slices/publicSlice';
import CustomModal from '../../../atoms/CustomModal';
import Button from '../../../atoms/Button';
import TitleText from '../../../atoms/TitleText';
import InfoText from '../../../atoms/InfoText';
import { TickSquare, TickCircle } from 'iconsax-react';
import { useApproveProductRequestMutation } from '../../../../store/services/products';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';

export default function ApproveProductModal() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { modalId, modalExtraData } = usePublicState();
  const [approveRequest, { isLoading }] = useApproveProductRequestMutation();
  const navigate = useNavigate();

  const isModalOpened = modalId === 'APPROVE_PRODUCT_MODAL';
  const productId = (modalExtraData as any)?.productId;

  const onClose = () => {
    dispatch(setModalId(null));
  };

  const onApprove = async () => {
    if (!productId) return;
    try {
      await approveRequest({ productId }).unwrap();
      message.success(t('home.approveRequest', 'Approved successfully'));
      onClose();
      navigate('/products');
    } catch (error) {
      const err = error as { data?: { errors?: { reason?: string } } };
      message.error(err?.data?.errors?.reason || t('home.somethingWentWrong'));
    }
  };

  return (
    <CustomModal
      isOpen={isModalOpened}
      onClose={onClose}
      title=""
      closable={false}
      centered
    >
      <div
        className="flex flex-col items-center text-center space-y-3 pt-4"
        data-testid="approve-modal-content"
      >
        <div
          className="w-[44px] h-[44px] bg-[#E7E7E7] dark:bg-gray-800 rounded-[12px] flex items-center justify-center"
          data-testid="approve-modal-icon-wrapper"
        >
          <TickSquare size={20} variant="Linear" color="#4DA167" />
        </div>

        <TitleText
          text={t('home.approveProduct')}
          size="2xl"
          className="!font-bold !text-[#111928] dark:!text-white !leading-none"
        />

        <InfoText
          className="!text-[#637381] dark:!text-gray-400 !font-normal !leading-5 text-center text-base"
          text={t('home.approveProductDesc')}
        />

        <div
          className="flex items-center justify-center !gap-6 w-full pt-3"
          data-testid="approve-modal-buttons"
        >
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex items-center justify-center !rounded-[12px] !border-1 !border-[#090909] dark:!border-gray-600 !text-[#090909] dark:!text-gray-200 !font-semibold w-[113px] !h-[46px] !px-[24px] !py-[12px] !gap-[8px]"
            size="md"
            data-testid="approve-modal-cancel-btn"
          >
            <span className="!text-base !leading-[22px] !font-semibold">
              {t('home.cancel')}
            </span>
          </Button>
          <Button
            size="md"
            type="button"
            onClick={onApprove}
            disabled={isLoading}
            icon={<TickCircle size={20} variant="Linear" color="#E7E7E7" />}
            className="!rounded-xl !font-semibold !px-6 !w-[253px] !py-3 !bg-[#4DA167] !text-white hover:!bg-[#3d8253] border-0"
            data-testid="approve-modal-confirm-btn"
          >
            <span className="!text-base !font-semibold !text-[#E7E7E7]">
              {' '}
              {t('home.approve')}
            </span>
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
